// Modal selector reutilizable para Personal / Proveedores / Servicios / Menú
import { useState, useEffect, useMemo } from "react";
import { Search, X, Plus, Check } from "lucide-react";

export const SelectorModal = ({
  abierto, onClose, titulo, items, onConfirmar,
  yaSeleccionados = [], getId, getNombre, getSubtitulo, getMeta, getAvatar, multi = true,
  emptyMsg = "Sin elementos en el catálogo",
  ctaCrear, onCrearNuevo,
}) => {
  const [search, setSearch] = useState("");
  const [seleccion, setSeleccion] = useState(new Set());

  useEffect(() => {
    if (abierto) setSeleccion(new Set(yaSeleccionados));
    if (!abierto) setSearch("");
  }, [abierto]);

  const filtrados = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return items;
    return items.filter(i => `${getNombre(i)} ${getSubtitulo?.(i) || ""}`.toLowerCase().includes(s));
  }, [items, search, getNombre, getSubtitulo]);

  if (!abierto) return null;

  const toggle = (id) => {
    setSeleccion(prev => {
      const n = new Set(prev);
      if (multi) {
        n.has(id) ? n.delete(id) : n.add(id);
      } else {
        n.clear();
        n.add(id);
      }
      return n;
    });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="selector-modal">
      <div className="w-full md:max-w-2xl max-h-[90vh] flex flex-col rounded-t-3xl md:rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--brand-border)" }}>
          <h3 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>{titulo}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--brand-surface-muted)]" data-testid="selector-cerrar"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 flex gap-2 items-center" style={{ borderBottom: "1px solid var(--brand-border)" }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="eo-input pl-10" autoFocus data-testid="selector-search" />
          </div>
          {onCrearNuevo && (
            <button onClick={onCrearNuevo} className="eo-btn eo-btn-outline shrink-0" data-testid="selector-crear">
              <Plus className="w-4 h-4" /> {ctaCrear || "Nuevo"}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {filtrados.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: "var(--brand-text-muted)" }}>{emptyMsg}</p>
          ) : (
            <div className="space-y-1.5">
              {filtrados.map(it => {
                const id = getId(it);
                const checked = seleccion.has(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggle(id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition"
                    style={{
                      background: checked ? "var(--brand-primary-soft)" : "transparent",
                      border: `1px solid ${checked ? "rgba(214,95,121,0.3)" : "var(--brand-border)"}`,
                    }}
                    data-testid={`item-${id}`}
                  >
                    {getAvatar ? (
                      getAvatar(it)
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0" style={{ background: checked ? "var(--brand-primary)" : "var(--brand-text-muted)" }}>
                        {(getNombre(it) || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{getNombre(it)}</p>
                      {getSubtitulo && <p className="text-xs truncate" style={{ color: "var(--brand-text-muted)" }}>{getSubtitulo(it)}</p>}
                    </div>
                    {getMeta && <span className="text-xs shrink-0" style={{ color: "var(--brand-text-muted)" }}>{getMeta(it)}</span>}
                    {checked && <Check className="w-4 h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 flex items-center justify-between gap-2" style={{ borderTop: "1px solid var(--brand-border)" }}>
          <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{seleccion.size} seleccionado{seleccion.size === 1 ? "" : "s"}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="eo-btn eo-btn-outline" data-testid="selector-cancelar">Cancelar</button>
            <button onClick={() => { onConfirmar(Array.from(seleccion)); onClose(); }} className="eo-btn eo-btn-primary" data-testid="selector-confirmar">
              <Check className="w-4 h-4" /> Confirmar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectorModal;
